/**
 * TRIL-635: the one place that decides how large an uploaded image may be.
 *
 * Shared because web and mobile both downscale before upload, and this arc has repeatedly shipped
 * a fix to one of a pair (web/mobile, post/reply composer, the two draft helpers, the editor and
 * the reader). Two copies of a dimension cap drift silently — one surface gets tuned and the other
 * keeps sending the old size, and nothing fails.
 *
 * WHY 2048. It is comfortably above every surface that displays an image today — a 180px editor
 * preview, a phone-width reader, a ~600px web column — and above what TRIL-620's enlarge will need
 * on any phone. So the downscale is invisible in use, while a 12-megapixel camera photo stops
 * being sent in full over cellular to paint a thumbnail.
 *
 * WHAT IT COSTS, stated plainly: the pre-upload original is gone. That is a real trade — it
 * forecloses generating larger derivatives later without a re-upload. It is acceptable only
 * because the bucket already caps at 3MB, so true originals were never being kept.
 *
 * This does NOT replace serving per-surface variants; a 180px preview still receives a 2048px
 * image. That needs a CDN in front of POST_MEDIA, which today has none — every read is a direct
 * S3 GET from us-east-2 regardless of where the reader is.
 */

/** Longest edge, in pixels, that an uploaded image may have. */
export const MAX_UPLOAD_DIMENSION = 2048;

/** JPEG/WebP quality for the re-encode. 0.8 is the usual knee: most of the saving, little visible loss. */
export const UPLOAD_IMAGE_QUALITY = 0.8;

/**
 * Target dimensions for an image, preserving aspect ratio. Returns null when it already fits —
 * callers must skip the re-encode in that case rather than round-tripping a small image through a
 * lossy codec for no reason.
 */
export function downscaleTarget(
  width: number,
  height: number,
  max: number = MAX_UPLOAD_DIMENSION,
): { width: number; height: number } | null {
  if (
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 0 ||
    height <= 0
  )
    return null;
  const longest = Math.max(width, height);
  if (longest <= max) return null;

  const scale = max / longest;
  return {
    // Rounded, and floored at 1: a very long thin image would otherwise scale its short edge to 0
    // and produce an unusable result.
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/**
 * The image types it is safe to decode and re-encode as JPEG.
 *
 * An ALLOWLIST, not a list of exclusions, and that choice is load-bearing twice over:
 *
 *  - ANIMATION. Re-encoding an animated GIF or WebP keeps only the first frame, silently turning
 *    a reaction GIF into a still. Both are in the server's accepted types, so both are reachable;
 *    detecting animation cheaply is not possible on either surface, so neither is re-encoded. The
 *    cost is that a large WebP photo uploads at full size — rare from a camera, and a missed
 *    optimisation is a far smaller harm than destroying an animation.
 *  - LAUNDERING. The downscale REWRITES the mime type to image/jpeg. If it accepted anything, an
 *    application/octet-stream would come out the other side as an accepted image/jpeg, past the
 *    client type check that runs after it. Only types that were already acceptable can be
 *    converted, so the downscale can never widen what is uploadable.
 *
 * SVG is absent for both reasons and a third: it is vector, so there are no pixels to cap.
 */
const REENCODABLE_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];

/**
 * Whether an image of this mime type may be downscaled. Anything else is uploaded untouched.
 *
 * The comparison is CASE-SENSITIVE, matching the client and server type checks exactly. It used
 * to lower-case, which quietly disagreed with them: an oversized `IMAGE/JPEG` was re-encoded and
 * so emerged as an accepted `image/jpeg`, while a small one of the same type was rejected — the
 * same upload allowed or refused depending on its dimensions. Two checks disagreeing about what
 * counts as an acceptable type is precisely the drift this module exists to stop, so it agrees
 * with the gate rather than being more generous than it.
 */
export function isReencodable(mimeType: string | null | undefined): boolean {
  return typeof mimeType === 'string' && REENCODABLE_TYPES.includes(mimeType);
}
